import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, InputAdornment, MenuItem, OutlinedInput, Paper, Popper, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// project import
import { useTheme } from '@mui/material/styles';
import { useGetModuleMutation } from 'store/features/menu/menuApi';
import { setSelectedModule } from 'store/features/menu/menuSlice';
import SafeClickAwayListener from 'components/@extended/SafeClickAwayListener';
import BreadcrumbTrail from './BreadcrumbTrail';

function NavContext() {
	const theme = useTheme();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const selectedModule = useSelector((state) => state.menu.selectedModule);
	const drawerOpen = useSelector((state) => state.menu.drawerOpen);

	const [getModule, { data: modulesResponse, isLoading: modulesLoading, isError: modulesError }] = useGetModuleMutation();

	const [selectedContext, setSelectedContext] = useState(null);
	const [contextAnchor, setContextAnchor] = useState(null);
	const [contextSearch, setContextSearch] = useState('');

	const contextAnchorRef = useRef(null);
	const contextSearchInputRef = useRef(null);

	const openContextMenu = (event) => {
		setContextSearch('');
		// Ensure we capture the button element
		const anchor = contextAnchorRef.current ?? event.currentTarget;
		setContextAnchor(anchor);
	};

	const closeContextMenu = () => {
		setContextAnchor(null);
		setContextSearch('');
	};

	const selectContext = (option) => {
		setSelectedContext(option);
		closeContextMenu();
		if (!option?.name) return;

		navigate(`/create`);

		dispatch(
			setSelectedModule({
				moduleCode: option.name,
				moduleName: option.title ?? option.name,
			})
		);

		// navigate(`/modules/${option.name}/insights`);
	};

	const pathSegments = pathname.split('/').filter(Boolean);
	const isModuleScoped = pathSegments[0] === 'modules' && Boolean(pathSegments[1]);

	const modules = useMemo(() => {
		const response = modulesResponse?.response ?? modulesResponse?.result?.response ?? [];
		return Array.isArray(response) ? response : [];
	}, [modulesResponse]);

	const globalContextOption = useMemo(
		() => ({
			id: 'GLOBAL',
			name: 'GLOBAL',
			title: 'All Modules',
			subtitle: 'All modules',
		}),
		[]
	);

	const contextOptions = useMemo(
		() => [
			globalContextOption,
			...modules
				.filter((module) => module?.name?.toUpperCase?.() !== 'GLOBAL')
				.map((module) => ({
					id: module?.id ?? module?._id ?? module?.name,
					name: module?.name,
					title: module?.displayName ?? module?.name ?? 'Module',
					subtitle: module?.shortDescription ?? '',
				})),
		],
		[globalContextOption, modules]
	);

	const filteredContextOptions = useMemo(() => {
		if (!contextSearch) return contextOptions;
		const query = contextSearch.toLowerCase().trim();
		if (!query) return contextOptions;

		const matchedOptions = contextOptions.filter((option) => {
			return `${option?.title ?? ''} ${option?.subtitle ?? ''} ${option?.name ?? ''}`.toLowerCase().includes(query);
		});

		return [...matchedOptions];
	}, [contextOptions, contextSearch]);

	useEffect(() => {
		getModule({});
	}, [getModule]);

	useEffect(() => {
		if (contextOptions.length === 0) return;

		if (selectedModule === 'GLOBAL' && !isModuleScoped) {
			setSelectedContext(globalContextOption);
			return;
		}

		const currentModuleName = isModuleScoped ? pathSegments?.[1] : null;
		const normalizedSelectedModule = selectedModule && selectedModule !== 'GLOBAL' ? selectedModule : null;
		const preferredModule = normalizedSelectedModule ?? currentModuleName;
		const matchedOption = preferredModule && contextOptions.find((option) => option?.name?.toLowerCase?.() === preferredModule?.toLowerCase?.());

		setSelectedContext((prev) => {
			if (matchedOption) return matchedOption;
			if (prev && contextOptions.some((option) => option?.id === prev?.id)) return prev;
			return globalContextOption;
		});
	}, [contextOptions, globalContextOption, isModuleScoped, pathname, selectedModule]);

	useEffect(() => {
		if (!contextAnchor) return;
		const timeoutId = setTimeout(() => {
			contextSearchInputRef.current?.focus?.();
		}, 0);
		return () => clearTimeout(timeoutId);
	}, [contextAnchor]);

	useEffect(() => {
		if (drawerOpen) return;
		closeContextMenu();
	}, [drawerOpen]);

	if (!drawerOpen) {
		return null;
	}

	return (
		<Box sx={{ p: 2.5, pb: 1.5 }}>
			<Typography
				sx={{
					fontSize: '0.625rem',
					fontWeight: 600,
					color: '#94A3B8',
					textTransform: 'uppercase',
					letterSpacing: '0.14em',
					mb: 1,
				}}
			>
				Active Module
			</Typography>
			<SafeClickAwayListener onClickAway={closeContextMenu}>
				<Box sx={{ mt: 0.75, position: 'relative' }}>
					<ButtonBase
						ref={contextAnchorRef}
						onClick={(event) => {
							if (contextAnchor) {
								closeContextMenu();
								return;
							}
							openContextMenu(event);
						}}
						aria-label="Select module"
						aria-haspopup="listbox"
						aria-expanded={Boolean(contextAnchor)}
						disabled={modulesLoading || modulesError}
						sx={{
							width: '100%',
							borderRadius: '10px',
							border: `1px solid ${theme.palette.border}`,
							backgroundColor: theme.palette.background.drawer,
							px: 1.25,
							py: 1,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							gap: 1,
							'&:hover': {
								backgroundColor: theme.palette.background.main,
							},
						}}
					>
						<Box sx={{ minWidth: 0 }}>
							<Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: theme.palette.text.primary }}>
								{selectedContext?.title ??
									(modulesLoading ? 'Loading modules…' : modulesError ? 'Modules unavailable' : 'Select a module')}
							</Typography>
							{/* <Typography noWrap sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
								{selectedContext?.subtitle ??
									(modulesLoading ? 'Please wait' : modulesError ? 'Try again later' : 'Choose from the list')}
							</Typography> */}
						</Box>
						<ExpandMoreIcon
							sx={{
								fontSize: 18,
								color: theme.palette.text.secondary,
								transform: contextAnchor ? 'rotate(180deg)' : 'rotate(0deg)',
								transition: 'transform 120ms',
							}}
						/>
					</ButtonBase>

					<Popper
						open={Boolean(contextAnchor)}
						anchorEl={contextAnchor}
						// placement="bottom-start"
						disablePortal
						// modifiers={[
						// 	{ name: 'offset', options: { offset: [0, 8] } },
						// 	{ name: 'preventOverflow', options: { padding: 8, altAxis: true } },
						// ]}
						sx={{ zIndex: theme.zIndex.drawer + 2, width: contextAnchor?.clientWidth ?? 'auto' }}
					>
						<Paper
							sx={{
								width: '100%',
								borderRadius: '12px',
								border: `1px solid ${theme.palette.border}`,
								backgroundColor: theme.palette.background.drawer,
								boxShadow: '0px 12px 40px rgba(15, 23, 42, 0.14)',
								overflow: 'hidden',
							}}
						>
							<Box sx={{ px: 1.25, pt: 1.25, pb: 1, borderBottom: `1px solid ${theme.palette.border}` }}>
								<OutlinedInput
									inputRef={contextSearchInputRef}
									value={contextSearch}
									onChange={(e) => setContextSearch(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === 'Escape') closeContextMenu();
									}}
									placeholder="Search modules…"
									size="small"
									fullWidth
									startAdornment={
										<InputAdornment position="start">
											<SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
										</InputAdornment>
									}
									sx={{
										'& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.border },
										'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
										'&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#93C5FD' },
										'& .MuiInputAdornment-root': { marginRight: 0 },
										backgroundColor: theme.palette.background.main,
									}}
								/>
							</Box>

							<Box sx={{ py: 0.5, maxHeight: 320, overflowY: 'auto', overflowX: 'hidden' }}>
								{modulesLoading && (
									<MenuItem disabled>
										<Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>Loading…</Typography>
									</MenuItem>
								)}

								{modulesError && !modulesLoading && (
									<MenuItem disabled>
										<Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>
											Unable to load modules
										</Typography>
									</MenuItem>
								)}

								{!modulesLoading &&
									!modulesError &&
									(filteredContextOptions.length === 0 ? (
										<MenuItem disabled>
											<Typography sx={{ fontSize: '0.875rem', color: theme.palette.text.secondary }}>No matches</Typography>
										</MenuItem>
									) : (
										filteredContextOptions.map((option) => {
											const isSelected = selectedContext?.id === option.id;
											return (
												<MenuItem
													key={option.id}
													onClick={() => selectContext(option)}
													selected={isSelected}
													sx={{
														mx: 0.75,
														my: 0.25,
														borderRadius: '10px',
														alignItems: 'flex-start',
														'&.Mui-selected': {
															backgroundColor: '#0F172A',
															'&:hover': {
																backgroundColor: '#0F172A',
															},
														},
														'&:hover': {
															backgroundColor: theme.palette.background.main,
														},
													}}
												>
													<Box sx={{ minWidth: 0 }}>
														<Typography
															noWrap
															sx={{
																fontSize: '0.875rem',
																color: isSelected ? '#FFFFFF' : theme.palette.text.primary,
															}}
														>
															{option.title}
														</Typography>
														{/* <Typography
															noWrap
															sx={{
																fontSize: '0.75rem',
																color: isSelected ? 'rgba(255,255,255,0.75)' : theme.palette.text.secondary,
															}}
														>
															{option.subtitle}
														</Typography> */}
													</Box>
												</MenuItem>
											);
										})
									))}
							</Box>
						</Paper>
					</Popper>
				</Box>
			</SafeClickAwayListener>

			<BreadcrumbTrail contextOptions={contextOptions} />
		</Box>
	);
}

export default NavContext;
